import React, { useEffect } from "react";
import { withStyles, createStyles, WithStyles, Theme } from "@material-ui/core/styles";

import Button from "@material-ui/core/Button";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";

import IconClose from "@material-ui/icons/Close";
import IconCheck from "@material-ui/icons/Check";

import { phlabeltype } from "../lib/storage/phlabel";
import { genericApi } from "../lib/genericAPI";

const styles = (theme: Theme) =>
  createStyles({
    root: {
      padding: `${theme.spacing.unit * 2}px`,
      maxWidth: 720,
      margin: "0 auto",
      width: "100%",
      boxSizing: "border-box",
    },
    header: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: `${theme.spacing.unit}px`,
      paddingBottom: `${theme.spacing.unit}px`,
      marginBottom: `${theme.spacing.unit * 2}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    title: {
      fontSize: "1.1rem",
      fontWeight: 600,
      color: theme.palette.text.primary,
    },
    actions: {
      display: "flex",
      gap: `${theme.spacing.unit}px`,
    },
    closeButton: {
      minWidth: "44px",
      backgroundColor: "#ffebee",
      borderColor: "#e53935",
      color: "#c62828",
      "&:hover": {
        backgroundColor: "#ffcdd2",
        borderColor: "#c62828",
      },
    },
    confirmButton: {
      minWidth: "44px",
      backgroundColor: "#43a047",
      color: "white",
      "&:hover": {
        backgroundColor: "#2e7d32",
      },
    },
    tableCard: {
      width: "100%",
      overflow: "hidden",
    },
    table: {
      width: "100%",
      borderCollapse: "collapse",
    },
    headRow: {
      backgroundColor: theme.palette.grey[100],
    },
    headCell: {
      textAlign: "left",
      fontSize: "12px",
      fontWeight: 600,
      letterSpacing: "0.04em",
      textTransform: "uppercase",
      color: theme.palette.text.secondary,
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 1.5}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
    },
    bodyRow: {
      "&:nth-child(even)": {
        backgroundColor: theme.palette.grey[50],
      },
    },
    bodyCell: {
      fontSize: "13px",
      padding: `${theme.spacing.unit}px ${theme.spacing.unit * 1.5}px`,
      borderBottom: `1px solid ${theme.palette.divider}`,
      color: theme.palette.text.primary,
      verticalAlign: "top",
    },
    emptyState: {
      textAlign: "center",
      padding: `${theme.spacing.unit * 4}px`,
      color: theme.palette.text.secondary,
      fontSize: "14px",
    },
  });

type InspectionPHLabelProps = {
  market: string;
  handleClose: () => void;
  handleConfirm: () => void;
} & WithStyles<typeof styles>;

const InspectionPHLabelUnstyled: React.SFC<InspectionPHLabelProps> = (props) => {
  const { classes, market, handleClose, handleConfirm } = props;
  const [phLabels, setPhLabels] = React.useState([]);

  useEffect(() => {
    const phlabelsGeneric = genericApi<phlabeltype>("phlabels");
    const resultArr = [];
    phlabelsGeneric.all().then((allData) => {
      if (allData && allData.length > 0) {
        allData.map((allDataItem) => {
          if (allDataItem.market == market) {
            resultArr.push(allDataItem);
          }
        });
        setPhLabels(resultArr);
      }
    });
  }, [classes]);

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography component="h2" className={classes.title}>
          Post-Harvest Labels
        </Typography>
        <div className={classes.actions}>
          <Button variant="outlined" size="small" className={classes.closeButton} onClick={handleClose}>
            <IconClose />
          </Button>
          <Button variant="contained" size="small" className={classes.confirmButton} onClick={handleConfirm}>
            <IconCheck />
          </Button>
        </div>
      </div>

      <Paper elevation={1} className={classes.tableCard}>
        <table className={classes.table}>
          <thead>
            <tr className={classes.headRow}>
              <th className={classes.headCell}>Item</th>
              <th className={classes.headCell}>Substance</th>
              <th className={classes.headCell}>Wording</th>
            </tr>
          </thead>
          <tbody>
            {phLabels.length === 0 && (
              <tr>
                <td className={classes.emptyState} colSpan={3}>
                  No post-harvest label requirements for this market.
                </td>
              </tr>
            )}
            {phLabels.map((phitem) => {
              return (
                <tr key={`det${phitem.id}`} className={classes.bodyRow}>
                  <td className={classes.bodyCell}>{phitem.item}</td>
                  <td className={classes.bodyCell}>{phitem.substance}</td>
                  <td className={classes.bodyCell}>{phitem.wording}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Paper>
    </div>
  );
};

export default withStyles(styles)(InspectionPHLabelUnstyled);
